import client from '@/shared/api/client';
import { isMockMode } from '@/__dev__/isMockMode';
import {
  mockRegisterInvitation,
  mockAddPassword,
  mockAcceptInvitation,
  mockFirstAccessIdentify,
} from '@/__dev__/mocks/invitation.mock';
import type {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  AddPasswordRequest,
  FirstAccessIdentifyRequest,
  FirstAccessIdentifyResponse,
  RegisterInvitationRequest,
  RegisterInvitationResponse,
} from '@/features/auth/types/invitation.types';

export async function registerInvitation(
  payload: RegisterInvitationRequest
): Promise<RegisterInvitationResponse> {
  if (isMockMode) return mockRegisterInvitation();
  const { data } = await client.post<RegisterInvitationResponse>(
    '/users/register-invitation',
    payload
  );
  return data;
}

/**
 * Set the password of an invited / first-access user.
 *
 * @param payload - New password
 * @param accessToken - Onboarding token issued by
 *   `/users/register-invitation` or `/auth/identify?firstaccess=true`
 */
export async function addPassword(
  payload: AddPasswordRequest,
  accessToken: string
): Promise<void> {
  if (isMockMode) return mockAddPassword();
  await client.post('/users/add-password', payload, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

/**
 * Accept invitation for existing users
 *
 * Called when existingUser=true, user is already registered
 * and just needs to accept the invitation.
 *
 * Authenticated by the stored session token sent by `client`.
 *
 * @param payload - Invitation credentials
 * @returns Promise with acceptance result
 */
export async function acceptInvitation(
  payload: AcceptInvitationRequest
): Promise<AcceptInvitationResponse> {
  if (isMockMode) return mockAcceptInvitation();
  const response = await client.post<AcceptInvitationResponse>(
    '/users/accept-invitation',
    payload
  );

  // API returns 201 with empty body on success
  if (response.status === 201) {
    return {
      success: true,
      message: 'Invitation accepted successfully',
    };
  }

  return response.data;
}

/**
 * Identifies a Business Account owner on first access
 *
 * Called when the user arrives via the onboarding email link.
 * Sends the username and OTP from the email URL to the identify endpoint
 * and receives a JWT token to be used for the password creation step.
 *
 * @param payload - Username and OTP from the email link
 * @returns Promise with JWT token data
 */
export async function firstAccessIdentify(
  payload: FirstAccessIdentifyRequest
): Promise<FirstAccessIdentifyResponse> {
  if (isMockMode) return mockFirstAccessIdentify();
  const { data } = await client.post<FirstAccessIdentifyResponse>(
    '/auth/identify',
    payload,
    { params: { firstaccess: true } }
  );
  return data;
}
